import { useCallback } from 'react';

import {
  getEmoneyIntegrationLoginPath,
  getEmoneyIntegrationLogoutPath,
} from '@/modules/assetProviderIntegrations/emoney/utils/emoneyAuthUtils';
import { ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES } from '@/modules/assetProviderIntegrations/shared/constants';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { IntegrationProvider } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { IntegrationRow } from '../IntegrationRow';

interface EmoneyCardProps {
  userAccountLinked: boolean;
  externalUserName?: string | null;
  integrationConfigured: boolean;
}

export function EmoneyCard({
  userAccountLinked,
  externalUserName,
  integrationConfigured,
}: EmoneyCardProps) {
  const canEditHouseholdData = useCanEditHouseholdData();
  const { assetIntegrationsEnabled } = useTenantDetailsContext();
  const currentUser = useCurrentUser();
  const isExpired =
    currentUser?.userExpiredIntegrationNames?.includes(
      ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.EMONEY
    ) ?? false;

  const getEmoneyButtonProps = useCallback(() => {
    if (
      !canEditHouseholdData ||
      !assetIntegrationsEnabled ||
      !integrationConfigured
    ) {
      return { disabled: true, href: '#', children: 'Link account' };
    }

    const currentPageURL = getCompletePathFromRouteKey('USER_SETTINGS', {});
    if (userAccountLinked) {
      return {
        href: getEmoneyIntegrationLogoutPath({ redirect: currentPageURL }),
        children: 'Unlink',
        method: 'post' as const,
      };
    } else {
      return {
        href: getEmoneyIntegrationLoginPath({ redirect: currentPageURL }),
        children: 'Link account',
      };
    }
  }, [
    canEditHouseholdData,
    assetIntegrationsEnabled,
    integrationConfigured,
    userAccountLinked,
  ]);

  return (
    <IntegrationRow
      integrationEnabled={userAccountLinked}
      isExpired={isExpired}
      externalUserName={externalUserName}
      description={
        !integrationConfigured ? 'Contact your administrator' : undefined
      }
      logo={
        <img
          alt="eMoney"
          height={24}
          src={getPublicImageUrl('/emoneyLogo.png')}
        />
      }
      buttonProps={getEmoneyButtonProps()}
      integrationProvider={IntegrationProvider.Emoney}
    />
  );
}
